import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixPaymentsTaxDocumentsV1TaxDocumentsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/tax-document-service/v1/tax-documents',
        destPath: '/v1/tax-documents',
      },
      {
        srcPath: '/_api/finance/wix-payments/v1/tax-documents',
        destPath: '/v1/tax-documents',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/tax-document-service/v1/tax-documents',
        destPath: '/v1/tax-documents',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/tax-document-service/v1/tax-documents',
        destPath: '/v1/tax-documents',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/tax-document-service/v1/tax-documents',
        destPath: '/v1/tax-documents',
      },
    ],
    'manage.base44.com': [
      {
        srcPath: '/_api/tax-document-service',
        destPath: '',
      },
    ],
    'payments.base44.com': [
      {
        srcPath: '/_api/tax-document-service',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/payments/platform/v1/tax-documents',
        destPath: '/v1/tax-documents',
      },
    ],
    _: [
      {
        srcPath: '/payments/platform/v1/tax-documents',
        destPath: '/v1/tax-documents',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/payments/platform/v1/tax-documents',
        destPath: '/v1/tax-documents',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_payments_tax-documents';

/** Retrieves a tax document by ID. */
export function getTaxDocument(payload: object): RequestOptionsFactory<any> {
  function __getTaxDocument({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.tax_documents.v1.tax_document',
      method: 'GET' as any,
      methodFqn:
        'wix.payments.tax_documents.v1.TaxDocumentsService.GetTaxDocument',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsTaxDocumentsV1TaxDocumentsServiceUrl({
        protoPath: '/v1/tax-documents/{taxDocumentId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'taxDocument.createdDate' },
              { path: 'taxDocument.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getTaxDocument;
}

/**
 * Retrieves a list of tax documents that belong to a Wix Payments account.
 *
 * You must specify the `accountId` of the account whose tax documents you want to retrieve.
 *
 * Tax documents are sorted in descending order by `period`, then by `createdDate`.
 *
 * When `type` is omitted, tax documents of all types are returned.
 */
export function listTaxDocuments(payload: object): RequestOptionsFactory<any> {
  function __listTaxDocuments({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.tax_documents.v1.tax_document',
      method: 'GET' as any,
      methodFqn:
        'wix.payments.tax_documents.v1.TaxDocumentsService.ListTaxDocuments',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsTaxDocumentsV1TaxDocumentsServiceUrl({
        protoPath: '/v1/tax-documents',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'taxDocuments.createdDate' },
              { path: 'taxDocuments.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listTaxDocuments;
}

/**
 * Generates temporary information for downloading a tax document's file.
 *
 * The returned `downloadInfo.url` is a temporary link to a PDF. It expires 10 seconds after it's generated, so follow it immediately, and don't store it as a static link. Call this method again to get a fresh URL.
 *
 * The tax document must have a file available, meaning its `downloadable` field is `true`.
 */
export function generateFileDownloadInfo(
  payload: object
): RequestOptionsFactory<any> {
  function __generateFileDownloadInfo({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.tax_documents.v1.tax_document',
      method: 'GET' as any,
      methodFqn:
        'wix.payments.tax_documents.v1.TaxDocumentsService.GenerateFileDownloadInfo',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsTaxDocumentsV1TaxDocumentsServiceUrl({
        protoPath:
          '/v1/tax-documents/{taxDocumentId}/generate-file-download-info',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'downloadInfo.urlExpirationDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __generateFileDownloadInfo;
}
